import {
  IMarketingInsight,
  IMarketingInsightPart,
  MarketingInsightSegment,
  marketingInsightJoin,
  marketingInsightPart,
} from './marketingInsightsModel';
import {
  IMarketingMoneyRow,
  marketingMoneyRows,
} from './marketingInspectorModel';
import {
  formatMarketingCount,
  formatMarketingMoney,
  formatMarketingSales,
  IMarketingCreativeSort,
  IMarketingSortOf,
  marketingMetricValue,
  marketingNumber,
  marketingSpendView,
  MARKETING_METRICS,
  MARKETING_NONE_KEY,
  MarketingCreativeSortKey,
} from './marketingModel';
import {
  IMarketingBreakdownRow,
  IMarketingMatrixCell,
  IMarketingMetrics,
  MarketingDimensionCode,
} from './types';

/**
 * ТЗ 0103 — «Реклама › Карта объявлений» (design-erp §3.3): точки пузырьков
 * и боковые списки из строк объявлений или кампаний. Модуль чистый: цвет —
 * кодом исхода, подписи переводит экран.
 */

/**
 * Фильтр объявлений панели фильтров («Объявления», `adf` адреса). Выбор
 * ищет через `marketingCreatives(search)`, фильтр уходит `adIds` (этап 2 ТЗ
 * 0103, design-backend §A3). Разбивка с ним — только по объявлениям и
 * форматам, матрица — только строками объявлений: иначе сервер отказывает
 * `ad_filter_dimension`, и экран говорит об этом словами.
 */
export const MARKETING_AD_FILTER_LIVE = true;

/** Разбивки, которые принимают фильтр объявлений. */
export const MARKETING_AD_FILTER_DIMENSIONS = ['AD', 'FORMAT'] as const;

/** Отказ «фильтр объявлений не у той разбивки» — код сервера. */
export const MARKETING_AD_FILTER_ERROR = 'ad_filter_dimension';

/** Уровень карты: объявления (`marketingCreatives`) или кампании (разрез). */
export const MARKETING_MAP_LEVELS = ['ads', 'campaigns'] as const;

export type MarketingMapLevel = typeof MARKETING_MAP_LEVELS[number];

/** Что по вертикали: CRM-лиды или продажи. */
export const MARKETING_MAP_Y_METRICS = ['crmLeads', 'sales'] as const;

export type MarketingMapYMetric = typeof MARKETING_MAP_Y_METRICS[number];

/**
 * Исход строки — цвет пузырька: есть продажи / лиды без продаж / расход без
 * лидов / без расхода.
 */
export const MARKETING_MAP_OUTCOMES = [
  'sales',
  'leadsNoSales',
  'noLeads',
  'noSpend',
] as const;

export type MarketingMapOutcome = typeof MARKETING_MAP_OUTCOMES[number];

/** Объявлений на карте (`marketingCreatives(orderBy -spendPln, limit 100)`). */
export const MARKETING_MAP_LIMIT = 100;

/** Строк в боковых списках «Лучшие по продажам» и «Тратят без результата». */
export const MARKETING_MAP_SIDE_LIMIT = 5;

export interface IMarketingMapRow {
  id: string;
  label: string;
  metrics: IMarketingMetrics;
}

export interface IMarketingMapPoint {
  id: string;
  label: string;
  /** Расход (полный или за собранные дни); `null` — неизвестен. */
  spend: number | null;
  leads: number;
  sales: number;
  outcome: MarketingMapOutcome;
}

export const marketingMapOutcome = (
  spend: number | null,
  leads: number,
  sales: number,
): MarketingMapOutcome => {
  if (sales > 0) return 'sales';
  if (leads > 0) return 'leadsNoSales';
  if (spend !== null && spend > 0) return 'noLeads';
  return 'noSpend';
};

/**
 * Исход строки таблицы «Рекламы» — та же классификация, что у точки карты
 * (точка в строке таблицы того же цвета, что на карте).
 */
export const marketingRowOutcome = (
  metrics: IMarketingMetrics,
): MarketingMapOutcome =>
  marketingMapOutcome(
    marketingSpendView(metrics).value,
    marketingMetricValue(metrics, 'crmLeads') ?? 0,
    marketingNumber(metrics.sales) ?? 0,
  );

/** Строки → точки карты (расход — как показан в таблице). */
export const marketingMapPoints = (
  rows: ReadonlyArray<IMarketingMapRow>,
): IMarketingMapPoint[] =>
  rows.map((row) => {
    const spend = marketingSpendView(row.metrics).value;
    const leads = marketingMetricValue(row.metrics, 'crmLeads') ?? 0;
    const sales = marketingNumber(row.metrics.sales) ?? 0;
    return {
      id: row.id,
      label: row.label,
      spend,
      leads,
      sales,
      outcome: marketingMapOutcome(spend, leads, sales),
    };
  });

/**
 * Медианный CPL точек с расходом и лидами — наклон направляющей
 * (лиды = расход / CPL). Нет таких точек — `null`.
 */
export const marketingMapMedianCpl = (
  points: ReadonlyArray<IMarketingMapPoint>,
): number | null => {
  const cpls = points
    .filter(
      (point) => point.spend !== null && point.spend > 0 && point.leads > 0,
    )
    .map((point) => (point.spend as number) / point.leads)
    .sort((a, b) => a - b);
  if (!cpls.length) return null;
  const mid = Math.floor(cpls.length / 2);
  return cpls.length % 2 ? cpls[mid] : (cpls[mid - 1] + cpls[mid]) / 2;
};

/** «Лучшие по продажам»: продажи ↓, при равенстве — дешевле. */
export const marketingMapBest = (
  points: ReadonlyArray<IMarketingMapPoint>,
  limit = MARKETING_MAP_SIDE_LIMIT,
): IMarketingMapPoint[] =>
  points
    .filter((point) => point.sales > 0)
    .sort(
      (a, b) =>
        b.sales - a.sales ||
        (a.spend ?? Number.POSITIVE_INFINITY) -
          (b.spend ?? Number.POSITIVE_INFINITY),
    )
    .slice(0, limit);

/** «Тратят без результата»: расход есть, ни лидов, ни продаж; расход ↓. */
export const marketingMapWasting = (
  points: ReadonlyArray<IMarketingMapPoint>,
  limit = MARKETING_MAP_SIDE_LIMIT,
): IMarketingMapPoint[] =>
  points
    .filter((point) => point.outcome === 'noLeads')
    .sort((a, b) => (b.spend ?? 0) - (a.spend ?? 0))
    .slice(0, limit);

/* ── «Что приносит продажи» (этап 2: `marketingMatrix` CAMPAIGN|AD × FAMILY) */

/** Переключатель графика: продажи, подписано PLN, CRM-лиды. */
export const MARKETING_SALES_CHART_METRICS = [
  'sales',
  'revenuePln',
  'crmLeads',
] as const;

export type MarketingSalesChartMetric = typeof MARKETING_SALES_CHART_METRICS[number];

/** Строк на графике «Что приносит продажи». */
export const MARKETING_SALES_CHART_LIMIT = 10;

/** Строк матрицы «Кампания × курс». */
export const MARKETING_CAMPAIGN_MATRIX_LIMIT = 15;

/** `orderBy` матрицы по метрике графика (итог строки ↓). */
export const marketingSalesChartOrder = (
  metric: MarketingSalesChartMetric,
): string => `-${metric}`;

export interface IMarketingSalesByRow {
  rows: Array<{ key: string; label: string; values: number[]; total: number }>;
  /** Слои — курсы (по сумме на графике ↓, «не определён» — последним). */
  segments: Array<{ key: string; label: string }>;
  /** Метрика строки `none` (продажи без кампании / объявления). */
  noneTotal: number;
}

const cellValue = (
  metrics: IMarketingMetrics,
  metric: MarketingSalesChartMetric,
): number => marketingNumber(metrics[metric]) ?? 0;

/**
 * Ячейки матрицы «кампания (или объявление) × курс» → строки стека: строку
 * `none` (продажи без кампании) график не рисует — её итог уходит в сноску
 * «ещё N продаж без кампании». Строки — по итогу метрики ↓, первые `limit`.
 */
export const marketingSalesByRow = (
  cells: ReadonlyArray<IMarketingMatrixCell>,
  metric: MarketingSalesChartMetric,
  limit = MARKETING_SALES_CHART_LIMIT,
): IMarketingSalesByRow => {
  const rowTotals = new Map<string, { label: string; total: number }>();
  let noneTotal = 0;
  cells.forEach((cell) => {
    const value = cellValue(cell.metrics, metric);
    if (cell.rowKey === MARKETING_NONE_KEY) {
      noneTotal += value;
      return;
    }
    const current = rowTotals.get(cell.rowKey) ?? {
      label: cell.rowLabel,
      total: 0,
    };
    current.total += value;
    rowTotals.set(cell.rowKey, current);
  });
  const shown = Array.from(rowTotals.entries())
    .filter(([, item]) => item.total > 0)
    .sort((a, b) => b[1].total - a[1].total || a[0].localeCompare(b[0]))
    .slice(0, limit);
  const shownKeys = new Set(shown.map(([key]) => key));
  const segmentTotals = new Map<string, { label: string; total: number }>();
  cells.forEach((cell) => {
    if (!shownKeys.has(cell.rowKey)) return;
    const current = segmentTotals.get(cell.colKey) ?? {
      label: cell.colLabel,
      total: 0,
    };
    current.total += cellValue(cell.metrics, metric);
    segmentTotals.set(cell.colKey, current);
  });
  const segments = Array.from(segmentTotals.entries())
    .filter(([, item]) => item.total > 0)
    .sort(
      (a, b) =>
        Number(a[0] === MARKETING_NONE_KEY) -
          Number(b[0] === MARKETING_NONE_KEY) ||
        b[1].total - a[1].total ||
        a[0].localeCompare(b[0]),
    )
    .map(([key, item]) => ({ key, label: item.label }));
  const byCell = new Map(
    cells.map((cell) => [`${cell.rowKey}|${cell.colKey}`, cell]),
  );
  return {
    rows: shown.map(([key, item]) => ({
      key,
      label: item.label,
      total: item.total,
      values: segments.map((segment) => {
        const cell = byCell.get(`${key}|${segment.key}`);
        return cell ? cellValue(cell.metrics, metric) : 0;
      }),
    })),
    segments,
    noneTotal,
  };
};

/**
 * «Только активные» (`act=1`): строки с расходом в периоде — минимум
 * расхода сервера (`minSpendPln`), отбирает до страниц.
 */
export const MARKETING_ACTIVE_MIN_SPEND = '0.01';

/** Отвергнет ли сервер разбивку с фильтром объявлений. */
export const marketingAdFilterBlocks = (
  adIds: ReadonlyArray<string> | null | undefined,
  dimension: string,
): boolean =>
  Boolean(adIds?.length) &&
  !(MARKETING_AD_FILTER_DIMENSIONS as readonly string[]).includes(dimension);

/* ── Таблица «Рекламы»: общая сортировка и подписи (повторное ревью A7) ─ */

/** Ключи, по которым сервер сортирует объявления (`marketingCreatives`). */
const CREATIVE_SORT_KEYS: ReadonlyArray<MarketingCreativeSortKey> = [
  'name',
  'ageDays',
  'spendPln',
  'impressions',
  'ctr',
  'cpm',
  'frequency',
  'reach28d',
  'cplPlatformPln',
  'crmLeads',
  'cplPln',
  'contractsSent',
  'sales',
  'roas',
  'hookRate',
  'fatigue',
];

const DELTA_PREFIX = 'delta_';

/**
 * Сортировка «Рекламы» одна на все группировки (A7): переход на
 * «Объявления» и обратно её не теряет. Для объявлений: «по изменению» —
 * по самому показателю (у объявлений «по изменению» нет), имя строки —
 * `name`; незнакомое объявлениям — без сортировки (по умолчанию сервера).
 */
export const marketingAdsCreativeSort = (
  sort: IMarketingSortOf<string> | null | undefined,
): IMarketingCreativeSort | null => {
  if (!sort) return null;
  const base = sort.key.startsWith(DELTA_PREFIX)
    ? sort.key.slice(DELTA_PREFIX.length)
    : sort.key;
  const key = base === 'label' ? 'name' : base;
  return (CREATIVE_SORT_KEYS as ReadonlyArray<string>).includes(key)
    ? { key: key as MarketingCreativeSortKey, desc: sort.desc }
    : null;
};

/**
 * Та же сортировка для разреза (каналы, кампании…): имя — `label`;
 * показатель, его «изменение» и покрытие — как есть; поля только объявлений
 * (возраст, частота, выгорание) — без сортировки.
 */
export const marketingAdsBreakdownSort = (
  sort: IMarketingSortOf<string> | null | undefined,
): IMarketingSortOf<string> | null => {
  if (!sort) return null;
  if (sort.key === 'name') return { key: 'label', desc: sort.desc };
  const base = sort.key.startsWith(DELTA_PREFIX)
    ? sort.key.slice(DELTA_PREFIX.length)
    : sort.key;
  if (base === 'label' || base === 'salesCoverage') {
    return base === sort.key ? sort : null;
  }
  return base in MARKETING_METRICS ? sort : null;
};

/** Разрезы, где строка есть только у известного объявления / группы. */
const AD_LEVEL_DIMENSIONS = ['ADSET', 'AD', 'FORMAT'];

/**
 * Подпись строки «Итого» таблицы «Рекламы» (A7): итог сервера не
 * подчиняется быстрым фильтрам (`having`, «Только активные»), а на уровне
 * групп / объявлений / форматов — только строки с известным объявлением
 * (77 лидов там против 299 в плитке). Ключ `marketing.columns.<ключ>`.
 */
export const marketingAdsTotalLabelKey = (
  dimension: string,
  filtered: boolean,
): 'total' | 'totalAllRows' | 'totalKnownAd' | 'totalKnownAdAllRows' => {
  const adLevel = AD_LEVEL_DIMENSIONS.includes(dimension);
  if (adLevel) return filtered ? 'totalKnownAdAllRows' : 'totalKnownAd';
  return filtered ? 'totalAllRows' : 'total';
};

/* ── Пакет 2 (P5, C5): «Реклама» проще — выводы, деньги, подписи карты ── */

/**
 * «Сколько вложили и сколько вернулось» (пакет 2): полосы — только у
 * платных каналов (шкала — по ним же: органика с большой выручкой не
 * сплющивает полосы рекламы), каналы без рекламного бюджета — одной строкой
 * «без рекламного бюджета: …» по убыванию подписанного.
 */
export interface IMarketingAdsMoneyView {
  paid: IMarketingMoneyRow[];
  noBudget: Array<{ key: string; revenue: number | null }>;
}

export const marketingAdsMoneyView = (
  rows: ReadonlyArray<IMarketingBreakdownRow>,
): IMarketingAdsMoneyView => {
  const all = marketingMoneyRows(rows);
  const paidKeys = new Set(all.filter((row) => row.paid).map((row) => row.key));
  return {
    paid: marketingMoneyRows(rows.filter((row) => paidKeys.has(row.key))),
    noBudget: all
      .filter((row) => !row.paid)
      .map((row) => ({ key: row.key, revenue: row.revenue })),
  };
};

/**
 * Строка проходит серверный предикат `SPEND_NO_LEADS` (тот же, что число на
 * чипе «Расход без лидов»): расход есть (полный или за собранные дни), а
 * CRM-лидов 0 — продажи не важны.
 */
export const marketingSpendNoLeads = (metrics: IMarketingMetrics): boolean =>
  (marketingSpendView(metrics).value ?? 0) > 0 &&
  (marketingNumber(metrics.crmLeads) ?? 0) === 0;

/**
 * Сумма «расхода без лидов» по строкам таблицы — только когда на странице
 * ВСЕ такие строки (их столько же, сколько на чипе сервера): иначе часть
 * осталась на других страницах, и сумма соврала бы. Не знаем — `null`.
 */
export const marketingAdsNoLeadsSpend = (
  rows: ReadonlyArray<IMarketingMetrics>,
  chipCount: number | null | undefined,
): number | null => {
  if (chipCount === null || chipCount === undefined) return null;
  const matching = rows.filter(marketingSpendNoLeads);
  if (matching.length !== chipCount) return null;
  return matching.reduce(
    (acc, metrics) => acc + (marketingSpendView(metrics).value ?? 0),
    0,
  );
};

/** Вершина «Что приносит продажи»: лидер (или лидеры поровну) и база. */
export interface IMarketingSalesTop {
  /** Подписи строк с наибольшим значением (несколько — поровну). */
  names: string[];
  value: number;
  /** Сумма показанных строк — база для «данных мало». */
  total: number;
}

/** Лидеров поровну в фразе — не больше; остальные — «ещё N». */
export const MARKETING_SALES_TOP_NAMES = 2;

/**
 * Лидер графика «Что приносит продажи» — из тех же строк, что рисует график
 * (`marketingSalesByRow`, по итогу ↓). Нет строк — `null`.
 */
export const marketingSalesTop = (
  view: Pick<IMarketingSalesByRow, 'rows'> | null | undefined,
): IMarketingSalesTop | null => {
  const rows = view?.rows ?? [];
  if (!rows.length) return null;
  const { total: value } = rows[0];
  return {
    names: rows.filter((row) => row.total === value).map((row) => row.label),
    value,
    total: rows.reduce((acc, row) => acc + row.total, 0),
  };
};

/**
 * Вершина графика для фразы вывода (финальная проверка 0103, F-E11): пока
 * едет ответ (смена показателя, перезапрос), на руках прошлый ответ
 * (`previousData`) с чужим порядком строк — лидера не называем
 * (`undefined`: фраза о графике молчит), иначе на миг называется не тот.
 */
export const marketingAdsSalesSummaryTop = (
  view: Pick<IMarketingSalesByRow, 'rows'> | null | undefined,
  fetching: boolean,
): IMarketingSalesTop | null | undefined =>
  view && !fetching ? marketingSalesTop(view) : undefined;

/** Меньше — «данных мало»: продаж (и лидов) на графике слишком мало. */
export const MARKETING_ADS_INSIGHT_MIN_SALES = 3;
export const MARKETING_ADS_INSIGHT_MIN_LEADS = 10;

/** Как назвать строки таблицы во фразе — по группировке. */
const ADS_ROWS_WORD: Readonly<Record<MarketingDimensionCode, string>> = {
  CHANNEL: 'marketing.insights.ads.rows.CHANNEL',
  CAMPAIGN: 'marketing.insights.ads.rows.CAMPAIGN',
  ADSET: 'marketing.insights.ads.rows.ADSET',
  AD: 'marketing.insights.ads.rows.AD',
  FAMILY: 'marketing.insights.ads.rows.other',
  OFFER: 'marketing.insights.ads.rows.other',
  FORMAT: 'marketing.insights.ads.rows.other',
};

const SALES_TOP_KEYS: Readonly<
  Record<MarketingSalesChartMetric, { one: string; tie: string }>
> = {
  sales: {
    one: 'marketing.insights.ads.top.sales',
    tie: 'marketing.insights.ads.top.salesTie',
  },
  revenuePln: {
    one: 'marketing.insights.ads.top.revenuePln',
    tie: 'marketing.insights.ads.top.revenuePlnTie',
  },
  crmLeads: {
    one: 'marketing.insights.ads.top.crmLeads',
    tie: 'marketing.insights.ads.top.crmLeadsTie',
  },
};

const SALES_SMALL_KEYS: Readonly<
  Record<'sales' | 'crmLeads', Record<'campaign' | 'ad', string>>
> = {
  sales: {
    campaign: 'marketing.insights.ads.small.sales.campaign',
    ad: 'marketing.insights.ads.small.sales.ad',
  },
  crmLeads: {
    campaign: 'marketing.insights.ads.small.crmLeads.campaign',
    ad: 'marketing.insights.ads.small.crmLeads.ad',
  },
};

const SALES_NONE_KEYS: Readonly<
  Record<MarketingSalesChartMetric, Record<'campaign' | 'ad', string>>
> = {
  sales: {
    campaign: 'marketing.insights.ads.none.sales.campaign',
    ad: 'marketing.insights.ads.none.sales.ad',
  },
  revenuePln: {
    campaign: 'marketing.insights.ads.none.revenuePln.campaign',
    ad: 'marketing.insights.ads.none.revenuePln.ad',
  },
  crmLeads: {
    campaign: 'marketing.insights.ads.none.crmLeads.campaign',
    ad: 'marketing.insights.ads.none.crmLeads.ad',
  },
};

/**
 * Что панель «Рекламы» знает о графике «Что приносит продажи» — таблицам
 * для вывода вкладки (тот же ответ, что рисует график).
 */
export interface IMarketingAdsSalesSummary {
  /** `null` — строк нет; `undefined` — график ещё едет. */
  top?: IMarketingSalesTop | null;
  metric: MarketingSalesChartMetric;
  kind: 'campaign' | 'ad';
}

export interface IMarketingAdsInsightInput {
  /**
   * Вершина графика «Что приносит продажи» (`marketingSalesTop`); `null` —
   * строк нет; `undefined` — график ещё едет.
   */
  top?: IMarketingSalesTop | null;
  /** Показатель графика — фраза говорит о том же, что график. */
  metric: MarketingSalesChartMetric;
  /** Строки графика — кампании или объявления (фильтр объявлений). */
  kind: 'campaign' | 'ad';
  /** Группировка таблицы — слово «кампаний / объявлений / строк». */
  dimension: MarketingDimensionCode;
  /** Число на чипе «Расход без лидов»; `null` — ещё не знаем. */
  noLeadsCount?: number | null;
  /** Сумма их расхода (`marketingAdsNoLeadsSpend`); `null` — не знаем. */
  noLeadsSpend?: number | null;
}

const formatChartValue = (
  value: number,
  metric: MarketingSalesChartMetric,
  lang: string,
): string => {
  if (metric === 'revenuePln') return formatMarketingMoney(value, lang);
  if (metric === 'sales') return formatMarketingSales(value, lang);
  return formatMarketingCount(value, lang);
};

const salesTopSegment = (
  { top, metric, kind }: IMarketingAdsInsightInput,
  lang: string,
): { part: IMarketingInsightPart; small: boolean } | null => {
  if (top === undefined) return null;
  if (top === null || top.value <= 0) {
    return {
      part: marketingInsightPart(SALES_NONE_KEYS[metric][kind]),
      small: true,
    };
  }
  if (metric !== 'revenuePln') {
    const floor =
      metric === 'sales'
        ? MARKETING_ADS_INSIGHT_MIN_SALES
        : MARKETING_ADS_INSIGHT_MIN_LEADS;
    if (top.total < floor) {
      return {
        part: marketingInsightPart(SALES_SMALL_KEYS[metric][kind], {
          n: formatChartValue(top.total, metric, lang),
        }),
        small: true,
      };
    }
  }
  const value = formatChartValue(top.value, metric, lang);
  if (top.names.length > 1) {
    // Поровну — первые два имени и «ещё N» (кавычки — у перевода: в ru
    // «…», в pl „…”).
    const [a, b] = top.names;
    const rest = top.names.length - MARKETING_SALES_TOP_NAMES;
    return {
      part: marketingInsightPart(SALES_TOP_KEYS[metric].tie, {
        names: marketingInsightPart(
          rest > 0
            ? 'marketing.insights.ads.namesMore'
            : 'marketing.insights.ads.namesTwo',
          { a, b, n: formatMarketingCount(rest, lang) },
        ),
        value,
      }),
      small: false,
    };
  }
  return {
    part: marketingInsightPart(SALES_TOP_KEYS[metric].one, {
      name: top.names[0],
      value,
    }),
    small: false,
  };
};

/**
 * Вывод «Рекламы» одной фразой (пакет 2, п. 1): «Больше всего продаж —
 * «Kurs Python» (7). Расход без CRM-лидов — объявлений: 4, на 1 342 PLN.»
 * Первая часть — вершина графика «Что приносит продажи» (тот же показатель,
 * что выбран на графике), вторая — число чипа «Расход без лидов» и сумма их
 * расхода из строк таблицы (если вся выборка на странице, иначе без суммы).
 * Мало продаж — так и говорит, лидера не называет. Ничего не знаем — `null`.
 */
export const marketingAdsInsight = (
  input: IMarketingAdsInsightInput,
  lang: string,
): IMarketingInsight | null => {
  const top = salesTopSegment(input, lang);
  const { noLeadsCount = null, noLeadsSpend = null, dimension } = input;
  let waste: IMarketingInsightPart | null = null;
  if (noLeadsCount !== null && noLeadsCount !== undefined) {
    if (noLeadsCount === 0) {
      waste = marketingInsightPart('marketing.insights.ads.noLeads.none');
    } else {
      const params = {
        what: marketingInsightPart(ADS_ROWS_WORD[dimension]),
        n: formatMarketingCount(noLeadsCount, lang),
      };
      waste =
        noLeadsSpend !== null && noLeadsSpend !== undefined
          ? marketingInsightPart('marketing.insights.ads.noLeads.withSpend', {
              ...params,
              spend: formatMarketingMoney(noLeadsSpend, lang),
            })
          : marketingInsightPart(
              'marketing.insights.ads.noLeads.count',
              params,
            );
    }
  }
  if (!top && !waste) return null;
  const segments: MarketingInsightSegment[] = marketingInsightJoin(
    [top ? top.part : null, waste],
    '. ',
  );
  let tone: IMarketingInsight['tone'] = 'neutral';
  if (noLeadsCount && noLeadsCount > 0) tone = 'warn';
  else if (top && !top.small) tone = 'good';
  return { tone, segments: [...segments, '.'] };
};

/* ── «Карта объявлений»: вывод и подписи крупнейших пузырей ───────────── */

/** Подписей на карте — у стольких крупнейших пузырей (продажи ↓). */
export const MARKETING_MAP_LABELS = 5;
/** Меньше точек с расходом и лидами — медиана CPL шаткая: «данных мало». */
export const MARKETING_MAP_INSIGHT_MIN_POINTS = 3;

/**
 * Какие пузыри подписать: крупнейшие — по продажам (радиус), при равенстве
 * — дешевле, ровно как «Лучшие по продажам» рядом (подписаны те же точки,
 * что в списке); только те, что на карте (расход известен, на лог-шкале —
 * больше 0). Продаж нет совсем — подписи у трёх самых дорогих.
 */
export const marketingMapLabelIds = (
  points: ReadonlyArray<IMarketingMapPoint>,
  { logX = false, limit = MARKETING_MAP_LABELS } = {},
): string[] => {
  const drawable = points.filter(
    (point) => point.spend !== null && (!logX || point.spend > 0),
  );
  const best = marketingMapBest(drawable, limit);
  if (best.length) return best.map((point) => point.id);
  return [...drawable]
    .sort((a, b) => (b.spend ?? 0) - (a.spend ?? 0) || a.id.localeCompare(b.id))
    .slice(0, Math.min(3, limit))
    .map((point) => point.id);
};

const MAP_WHAT: Readonly<Record<MarketingMapLevel, string>> = {
  ads: 'marketing.insights.map.what.ads',
  campaigns: 'marketing.insights.map.what.campaigns',
};

export interface IMarketingMapInsightInput {
  /** Точки карты — те же, что нарисованы. */
  points: ReadonlyArray<IMarketingMapPoint>;
  /** Медиана CPL — та же, что у диагонали (`marketingMapMedianCpl`). */
  medianCpl: number | null;
  level: MarketingMapLevel;
}

/**
 * Вывод карты одной фразой (пакет 2, п. 1): «Без лидов потрачено 1 185 PLN —
 * объявлений: 4; медианный CPL — 120 PLN.» Красные точки (расход без лидов
 * и продаж) и медиана диагонали — из тех же точек. Точек с лидами меньше
 * трёх — медиана с пометкой «данных мало»; точек нет — `null` (пусто
 * говорит сама карта).
 */
export const marketingMapInsight = (
  { points, medianCpl, level }: IMarketingMapInsightInput,
  lang: string,
): IMarketingInsight | null => {
  if (!points.length) return null;
  const wasting = points.filter((point) => point.outcome === 'noLeads');
  const spend = wasting.reduce((acc, point) => acc + (point.spend ?? 0), 0);
  const waste = wasting.length
    ? marketingInsightPart('marketing.insights.map.noLeads', {
        spend: formatMarketingMoney(spend, lang),
        what: marketingInsightPart(MAP_WHAT[level]),
        n: formatMarketingCount(wasting.length, lang),
      })
    : marketingInsightPart('marketing.insights.map.noLeadsNone');
  const base = points.filter(
    (point) => point.spend !== null && point.spend > 0 && point.leads > 0,
  ).length;
  let median: IMarketingInsightPart;
  if (medianCpl === null) {
    median = marketingInsightPart('marketing.insights.map.noMedian');
  } else if (base < MARKETING_MAP_INSIGHT_MIN_POINTS) {
    median = marketingInsightPart('marketing.insights.map.medianSmall', {
      cpl: formatMarketingMoney(medianCpl, lang),
      n: formatMarketingCount(base, lang),
    });
  } else {
    median = marketingInsightPart('marketing.insights.map.median', {
      cpl: formatMarketingMoney(medianCpl, lang),
    });
  }
  let tone: IMarketingInsight['tone'] = 'good';
  if (wasting.length) tone = 'warn';
  else if (medianCpl === null || base < MARKETING_MAP_INSIGHT_MIN_POINTS) {
    tone = 'neutral';
  }
  return { tone, segments: [waste, '; ', median, '.'] };
};
